"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";

const LONG_PARAGRAPH_WORDS = 200;
const SHORT_PARAGRAPH_WORDS = 25;

// Module-level regexes (Biome: regexes must not live inside functions/loops).
const RE_PARAGRAPH_SPLIT = /\n[ \t]*\n+/;
const RE_WORD_SPLIT = /\s+/;
const RE_SENTENCE_END = /[.!?]+(?=\s|$)/g;

interface ParagraphStats {
  index: number;
  words: number;
  sentences: number;
  preview: string;
  flag: "long" | "short" | null;
}

interface Report {
  paragraphs: ParagraphStats[];
  totalWords: number;
  avgWords: number;
  minWords: number;
  maxWords: number;
  avgSentences: number;
}

function countWords(text: string): number {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return 0;
  }
  return trimmed.split(RE_WORD_SPLIT).length;
}

function countSentences(text: string): number {
  const matches = text.match(RE_SENTENCE_END);
  if (!matches || matches.length === 0) {
    return text.trim().length > 0 ? 1 : 0;
  }
  return matches.length;
}

function lengthFlag(words: number): "long" | "short" | null {
  if (words > LONG_PARAGRAPH_WORDS) {
    return "long";
  }
  if (words < SHORT_PARAGRAPH_WORDS) {
    return "short";
  }
  return null;
}

function previewOf(text: string): string {
  const flat = text.split(RE_WORD_SPLIT).join(" ").trim();
  if (flat.length <= 90) {
    return flat;
  }
  return `${flat.slice(0, 90).trimEnd()}…`;
}

function analyze(text: string): Report {
  const blocks = text
    .trim()
    .split(RE_PARAGRAPH_SPLIT)
    .map((block) => block.trim())
    .filter(Boolean);
  const paragraphs: ParagraphStats[] = blocks.map((block, index) => {
    const words = countWords(block);
    return {
      index: index + 1,
      words,
      sentences: countSentences(block),
      preview: previewOf(block),
      flag: lengthFlag(words),
    };
  });
  const wordCounts = paragraphs.map((p) => p.words);
  const totalWords = wordCounts.reduce((sum, n) => sum + n, 0);
  const totalSentences = paragraphs.reduce((sum, p) => sum + p.sentences, 0);
  const count = paragraphs.length;
  return {
    paragraphs,
    totalWords,
    avgWords: count > 0 ? Math.round(totalWords / count) : 0,
    minWords: count > 0 ? Math.min(...wordCounts) : 0,
    maxWords: count > 0 ? Math.max(...wordCounts) : 0,
    avgSentences:
      count > 0 ? Math.round((totalSentences / count) * 10) / 10 : 0,
  };
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

function FlagBadge({
  flag,
  longLabel,
  shortLabel,
}: {
  flag: "long" | "short";
  longLabel: string;
  shortLabel: string;
}) {
  const cls =
    flag === "long"
      ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200"
      : "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200";
  const label = flag === "long" ? longLabel : shortLabel;
  return (
    <span className={`rounded-full px-2 py-0.5 font-medium text-xs ${cls}`}>
      {label}
    </span>
  );
}

function ParagraphRow({
  paragraph,
  locale,
  strings,
}: {
  paragraph: ParagraphStats;
  locale: string;
  strings: {
    paragraphLabel: string;
    wordsCount: string;
    sentencesCount: string;
    paragraphStats: string;
    longLabel: string;
    shortLabel: string;
  };
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-foreground text-sm">
          {strings.paragraphLabel} {paragraph.index}
        </p>
        <span className="text-muted-foreground text-xs">
          {t(strings.paragraphStats, {
            words: tPlural(strings.wordsCount, locale, paragraph.words),
            sentences: tPlural(
              strings.sentencesCount,
              locale,
              paragraph.sentences
            ),
          })}
        </span>
        {paragraph.flag ? (
          <FlagBadge
            flag={paragraph.flag}
            longLabel={strings.longLabel}
            shortLabel={strings.shortLabel}
          />
        ) : null}
      </div>
      <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
        {paragraph.preview}
      </p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const PARAGRAPH_COUNTER_STRINGS = {
  inputLabel: "Your text — separate paragraphs with a blank line",
  inputPlaceholder:
    "Paste or type your essay here...\n\nEach paragraph separated by a blank line is counted separately.",
  clear: "Clear",
  paragraphs: "Paragraphs",
  totalWords: "Total words",
  avgWordsPerParagraph: "Avg words / paragraph",
  shortestParagraph: "Shortest paragraph",
  longestParagraph: "Longest paragraph",
  avgSentencesPerParagraph: "Avg sentences / paragraph",
  paragraphBreakdown: "Paragraph breakdown",
  flaggedParagraphs:
    "one:{count} paragraph flagged for length|other:{count} paragraphs flagged for length",
  paragraphLabel: "Paragraph",
  wordsCount: "one:{count} word|other:{count} words",
  sentencesCount: "one:{count} sentence|other:{count} sentences",
  paragraphStats: "{words} · {sentences}",
  longLabel: "Long (over {count} words)",
  shortLabel: "Short (under {count} words)",
} as const;

export default function ParagraphCounterWidget() {
  const [text, setText] = useState("");
  const strings = useToolUi(PARAGRAPH_COUNTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();

  const hasInput = text.trim().length > 0;
  const report = analyze(text);
  const flagged = report.paragraphs.filter((p) => p.flag !== null).length;

  const longLabel = t(strings.longLabel, { count: LONG_PARAGRAPH_WORDS });
  const shortLabel = t(strings.shortLabel, { count: SHORT_PARAGRAPH_WORDS });

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="paragraph-input"
        >
          {strings.inputLabel}
        </label>
        <textarea
          className="min-h-[220px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="paragraph-input"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={text}
        />
        {hasInput ? (
          <button
            className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
            onClick={() => setText("")}
            type="button"
          >
            {strings.clear}
          </button>
        ) : null}
      </div>

      {/* Stats */}
      {hasInput ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.paragraphs}
            value={fmt(report.paragraphs.length)}
          />
          <StatCard label={strings.totalWords} value={fmt(report.totalWords)} />
          <StatCard
            label={strings.avgWordsPerParagraph}
            value={fmt(report.avgWords)}
          />
          <StatCard
            label={strings.shortestParagraph}
            value={fmt(report.minWords)}
          />
          <StatCard
            label={strings.longestParagraph}
            value={fmt(report.maxWords)}
          />
          <StatCard
            label={strings.avgSentencesPerParagraph}
            value={fmt(report.avgSentences, { maximumFractionDigits: 1 })}
          />
        </div>
      ) : null}

      {/* Per-paragraph breakdown */}
      {hasInput ? (
        <section aria-live="polite" className="space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-semibold text-foreground text-xl">
              {strings.paragraphBreakdown}
            </h2>
            {flagged > 0 ? (
              <p className="text-muted-foreground text-sm">
                {tPlural(strings.flaggedParagraphs, locale, flagged, {
                  count: fmt(flagged),
                })}
              </p>
            ) : null}
          </div>
          <div className="space-y-3">
            {report.paragraphs.map((paragraph) => (
              <ParagraphRow
                key={paragraph.index}
                locale={locale}
                paragraph={paragraph}
                strings={{
                  paragraphLabel: strings.paragraphLabel,
                  wordsCount: strings.wordsCount,
                  sentencesCount: strings.sentencesCount,
                  paragraphStats: strings.paragraphStats,
                  longLabel,
                  shortLabel,
                }}
              />
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}
